import { logContentCardImpressions, logContentCardClick, getCachedContentCards } from '@braze/web-sdk';
import { isBrazeReady } from './brazeReady';

/**
 * Get the Braze Card instance from cached content cards.
 * Braze SDK requires Card class instances, not plain objects.
 *
 * @param {Object} cardData - The content card data object
 * @returns {Object|null} - The Braze Card instance or null if not found
 */
export const getBrazeCard = (cardData) => {
  // If cardData has rawCardData property (indicates it's a Braze Card instance),
  // return it directly
  if (cardData && cardData.rawCardData) {
    return cardData;
  }

  // If cardData has an id, try to find it in the cache
  if (cardData && cardData.id && isBrazeReady()) {
    try {
      const cachedCards = getCachedContentCards();
      if (cachedCards && cachedCards.cards) {
        return cachedCards.cards.find(c => c.id === cardData.id) || null;
      }
    } catch (e) {
      // Silently fail - Braze may not be available
    }
  }

  return null;
};

/**
 * Track a content card impression event.
 * Works with both plain objects and Braze Card instances.
 *
 * @param {Object} cardData - The content card data object
 */
export const trackContentCardImpression = (cardData) => {
  if (!cardData || !isBrazeReady()) return;

  const brazeCard = getBrazeCard(cardData);

  if (brazeCard) {
    logContentCardImpressions([brazeCard], true);
  } else if (cardData.id) {
    // eslint-disable-next-line no-console
    console.warn('[Braze] trackContentCardImpression skipped: failed to resolve Braze Card instance', {
      id: cardData?.id,
      url: cardData?.url || cardData?.u,
      hasRawCardData: Boolean(cardData?.rawCardData),
    });
  }
};

/**
 * Track a content card click event.
 * Works with both plain objects and Braze Card instances.
 *
 * @param {Object} cardData - The content card data object
 */
export const trackContentCardClick = (cardData) => {
  if (!cardData || !isBrazeReady()) return;

  const brazeCard = getBrazeCard(cardData);

  if (brazeCard) {
    logContentCardClick(brazeCard, true);
  } else if (cardData.id) {
    // eslint-disable-next-line no-console
    console.warn('[Braze] trackContentCardClick skipped: failed to resolve Braze Card instance', {
      id: cardData?.id,
      url: cardData?.url || cardData?.u,
      hasRawCardData: Boolean(cardData?.rawCardData),
    });
  }
};
